import {
    AuthenticationDetails,
    CognitoUser,
    CognitoUserPool,
    CognitoUserSession,
} from 'amazon-cognito-identity-js';

import { config } from './config';

const pool = new CognitoUserPool({ UserPoolId: config.userPoolId, ClientId: config.userPoolClientId });

const DELIVER_SUPPRESS = { deliver: 'false' };

const cognitoUser = (email: string): CognitoUser => {
    const user = new CognitoUser({ Username: email, Pool: pool });
    user.setAuthenticationFlowType('CUSTOM_AUTH');
    return user;
};

/** Cognito requires a password on the user record even though we never use it.
 *  A throwaway value satisfies the API; sign-in is purely custom-auth. */
const throwawayPassword = (): string => `Aa1!${crypto.randomUUID()}`;

/** First login auto-creates the shell user (PreSignUp auto-confirms). Treat an
 *  existing user as success. */
const ensureUser = (email: string): Promise<void> =>
    new Promise((resolve, reject) => {
        pool.signUp(email, throwawayPassword(), [], [], (err) => {
            if (!err || err.name === 'UsernameExistsException') return resolve();
            reject(err);
        });
    });

const getIdToken = (session: CognitoUserSession): string => session.getIdToken().getJwtToken();

/** Start a code-based sign-in: emails the link + code, then resolves with a
 *  function the caller invokes with the user-entered code. */
export const startCodeSignIn = async (email: string): Promise<(code: string) => Promise<string>> => {
    await ensureUser(email);
    const user = cognitoUser(email);

    return new Promise((resolveStart, rejectStart) => {
        user.initiateAuth(new AuthenticationDetails({ Username: email }), {
            onSuccess: () => rejectStart(new Error('Unexpected immediate success')),
            onFailure: rejectStart,
            customChallenge: () => {
                resolveStart(
                    (code: string) =>
                        new Promise<string>((resolveAnswer, rejectAnswer) => {
                            user.sendCustomChallengeAnswer(code, {
                                onSuccess: (session) => resolveAnswer(getIdToken(session)),
                                onFailure: rejectAnswer,
                                customChallenge: () => rejectAnswer(new Error('Incorrect code')),
                            });
                        }),
                );
            },
        });
    });
};

/** Complete a magic-link sign-in. Reuses the already-emailed challenge
 *  (deliver=false) and submits the link token as the answer. */
export const completeMagicLink = async (email: string, token: string): Promise<string> => {
    await ensureUser(email);
    const user = cognitoUser(email);

    return new Promise((resolve, reject) => {
        user.initiateAuth(new AuthenticationDetails({ Username: email, ClientMetadata: DELIVER_SUPPRESS }), {
            onSuccess: () => reject(new Error('Unexpected immediate success')),
            onFailure: reject,
            customChallenge: () => {
                user.sendCustomChallengeAnswer(
                    token,
                    {
                        onSuccess: (session) => resolve(getIdToken(session)),
                        onFailure: reject,
                        customChallenge: () => reject(new Error('Expired or invalid link')),
                    },
                    DELIVER_SUPPRESS,
                );
            },
        });
    });
};

/** Resolve a still-valid id token from a persisted session (silent re-auth),
 *  or null if the visitor must sign in again. */
export const currentIdToken = (): Promise<string | null> =>
    new Promise((resolve) => {
        const user = pool.getCurrentUser();
        if (!user) return resolve(null);
        user.getSession((err: Error | null, session: CognitoUserSession | null) => {
            if (err || !session?.isValid()) return resolve(null);
            resolve(getIdToken(session));
        });
    });

export const signOut = (): void => pool.getCurrentUser()?.signOut();
